import type { ToastPluginApi } from 'vue-toast-notification';
import VueToast              from 'vue-toast-notification';
import router                from '@/router';
import App                   from '@/App.vue';
import store                 from '@/store';
//
import TimeStamp             from '@/core/types/TimeStamp';
//
import Constants             from '@/core/misc/Constants';
import Config                from '@/core/misc/Config';
import AutoRegisterPlugin    from "@/core/misc/AutoRegisterPlugin";
import ErrorCodes            from '@/core/misc/ErrorCodes';
import ApplicationError      from '@/core/misc/ApplicationError';
import { ErrorSeverity }     from '@/core/misc/ErrorSeverity';
import CriticalErrorPage     from '@/core/misc/CriticalErrorPage';
import EventBus              from '@/core/services/EventBus/EventBus';
import Utils                 from '@/core/utils/Utils';
import LiveTime              from '@/core/misc/LiveTime';
import Loader                from "@/core/misc/Loader";
import SentryService         from '@/core/services/Sentry/SentryService';
import SentryBreadcrumbList  from '@/core/services/Sentry/SentryBreadcrumbList';
import RpcService            from '@/core/services/Rpc/RpcService';
import AclService            from '@/core/services/Acl/AclService';
import DialogsRegistry       from '@/core/services/Dialogs/DialogsRegistry';
import FilesService          from '@/core/services/Files/FilesService';
import BuildVersionChecker   from '@/core/services/BuildVersionChecker/BuildVersionChecker';
import Dictionaries          from '@/core/services/Dictionaries/Dictionaries';
import DialogsRouter         from "@/core/services/Dialogs/DialogsRouter";
import TooltipService        from "@/core/services/TooltipService";
import GlobalLoader          from "@/core/services/GlobalLoader/GlobalLoader";
import CurrenciesService     from '@/domains/Currencies/CurrenciesService';
import TimeZone              from "@/domains/TimeZones/TimeZone";

import type {App as VueApp}                     from 'vue';
import { createApp, h }                         from 'vue';

import SentryBreadcrumb, { BreadcrumbCategory } from "@/core/services/Sentry/SentryBreadcrumb";
import ErrorLevel                               from "@/core/services/Sentry/ErrorLevel";
import AppHeaderService from "@/lib/components/AppHeader/AppHeaderService";

/**
 * Ядро приложения (Application Core)
 *
 * Объединяет несколько паттернов для удобства разработки:
 * - Service Locator: централизованный доступ к сервисам через геттеры
 * - Facade: упрощенные shortcuts для частых операций
 * - Application Controller: управление инициализацией и жизненным циклом
 *
 * Сознательно совмещает роли для минимизации boilerplate кода
 * и предоставления единого entry point для всей системы.
 */
class Core {
	/**
	 * Экземпляр Vue приложения
	 */
	static vue: VueApp;

	/**
	 * Текущая временная зона пользователя
	 */
	static #timeZone: TimeZone | null = null;

	/**
	 * Текущее время с настраиваемой частотой обновления
	 */
	static #liveTime: LiveTime;

	/**
	 * Флаг отправки `debug: true` в теле RPC-запроса.
	 * Хранится в localStorage. Имя `debug` занято методом-логгером.
	 */
	static debugMode = ref(Core.#loadDebugMode());

	/**
	 * Ссылки-синонимы системных сервисов и утилит для очевидности их наличия
	 */
	static get config()                                { return Config; }
	static get sentry()                                { return SentryService; }
	static get eventBus()                              { return EventBus; }
	static get rpc()                                   { return RpcService; }
	static get acl()                                   { return AclService; }
	static get dialogs()                               { return DialogsRegistry; }
	static get tooltip()                               { return TooltipService; }
	static get currencies()                            { return CurrenciesService; }
	static get dictionaries()                          { return Dictionaries; }
	static get router()                                { return router; }
	static get dialogRouter()                          { return DialogsRouter; }

	static get currentUser()                           { return AclService.currentUser; }
	static get currentPartner()                        { return AclService.currentPartner; }
	static get displayedCurrencyCode()                 { return AclService.displayedCurrencyCode; }
	static get sibWellPartner()                        { return Dictionaries.sibWellPartner; }

	static get utils()                                 { return Utils; }
	static get constants()                             { return Constants; }
	static get loader()                                { return Loader; }
	static get confirm()                               { return DialogsRegistry.confirm; }
	static get confirmWithReason()                     { return DialogsRegistry.confirmWithReason; }

  static get liveTime()                              { return this.#liveTime; }
	static get legacyRpc()                             { return RpcService.makeLegacyRequest.bind(RpcService); }

	static logException(error: Error): void            { SentryService.reportError(error); }

	static addBreadcrumb(messageOrPayload: string | any, payload?: any): void { SentryBreadcrumbList.trace(messageOrPayload, payload); }

	static async initialize(): Promise<void> {
        if (this.vue) {
			this.error('Приложение уже инициализировано');
			return;
		}
		try {
      		const breadcrump = SentryBreadcrumb.create('init path', BreadcrumbCategory.Navigation, ErrorLevel.Info, { href: window.location.href })
      		SentryBreadcrumbList.add(breadcrump)

			this.debug('Инициализация приложения...');
			// Настраиваем глобальные объекты
			this.#initGlobals();
			// Инициализируем конфигурацию
			await Config.init();
			// Инициализируем Sentry сервис — передаём колбэк для обработки ApplicationError
			SentryService.init(Core.handleApplicationError.bind(Core));
			// инициализируем тайм зону
			this.#initTimeZone();
			// инициализируем Vue
			this.#initVueApp();
			// Инициализируем EventBus
			await EventBus.init();
			// иницилизация валютного сервиса
			await CurrenciesService.init();
			// Проверка версии сборки: сервис сам подпишется на ACL-события.
			// Важно сделать это до AclService.init(), иначе первое событие
			// логина/восстановления сессии пролетит мимо подписки.
			BuildVersionChecker.init();
			// инициализация rpc (инверсия зависимостей)
			await RpcService.init(
				GlobalLoader.show,
				GlobalLoader.hide,
				Core.handleRpcError.bind(Core),
				() => Core.showNotification,
				() => Core.timeZone,
				() => AclService.sessionToken,
				SentryBreadcrumbList.addCustom.bind(SentryBreadcrumbList)
			);
			// инициализируем ACL
			await AclService.init();

			/** Инициализация справочников здесь (очень хитро сделано!), потому что
			 * мало просто подписаться на событие ACL_LOGIN и ACL_SESSION_RESTORED.
			 * ACL_SESSION_RESTORED случится, мы запросим по этому событию справочники,
			 * и пока мы их ждём, случится APP_READY. Мы покажем пользователю интерфейс, например
			 * создания офера и не сможем инициализировать офер без справочников - ошибка.
			 **/
			// Инициализируем все словари
			await Dictionaries.init();
      		// При переходе из аккаунта в аккаунт удобно когда воронка сама меняется. Например ОЗЛ -> Поставщик
      		AppHeaderService.init()

			// инициализируем "часы"
			this.#initLiveTime();

			/** В подписку это не вынести, я пробовал, даже once не помогает,
			 *  потому что нужно повешать колбек сразу на acl:login и acl:restore,
			 *  а эти два события запросто происходят в течении работы **/
			// Очень важно подключить роутер здесь, после инициализации ACL, потому что
			// при релоаде страницы, мы запрашиваем текущего пользователя по sessionToken
			// И если подключать роутер раньше, то мы, не успев получить уже залогиненого пользователя,
			// отправим его на страницу логина
			this.vue.use(router);

            // Подключаем Sentry к Vue и роутеру
			SentryService.setupVueErrorHandler(this.vue);
			SentryService.setupRouterTracking(router);

			// Сервис навигации кушает роутер
			await DialogsRouter.init(router);

			/** После того как мы подключили роутер, монтируем приложение,
			 * в противном случае тег router-view не знаком для vue, и падает ворнинг **/
			// Монтируем приложение
			this.vue.mount('#app');
			//this.debug(`📱 Vue приложение создано`);

			// менеджер диалогов, async не имеет смысла
			DialogsRegistry.init();

			// сервис управляющий хранилищем файлов
			await FilesService.init();

			// Регистрируем Core в Vue для доступа через DevTools
			this.#registerCoreInVue();

			// Генерируем событие готовности приложения
			EventBus.AppReady.emit();

			//this.debug('Приложение успешно инициализировано');
		} catch (criticalError) {
			this.error('Ошибка инициализации приложения:', criticalError as Error);

			// Генерируем событие ошибки приложения, если EventBus доступен
			if (EventBus.isInitialized) {
				EventBus.AppError.emit();
			}

			this.error('Критическая ошибка:', criticalError as Error, true);
			CriticalErrorPage.show(criticalError as Error);
		}
	}

	/**
	 * Настройка глобальных объектов, прототипов и подписок
	 */
	static #initGlobals(): void {
		// Переопределяем toJSON для Date
		Date.prototype.toJSON = TimeStamp.prototype.toJSON;
	}

	static #initVueApp(): void {
		// Создаем Vue 3 приложение
		this.vue = createApp({
			/** Код ниже даёт возможность увидеть заветное слово core в дев тулзах в Root **/
			data() {
				return {
					core: Core
				};
			},
			render: () => h(App),
		});

		// Настраиваем плагины перед созданием
		this.#initVuePlugins();

		// Роутер используем в другом месте, если его
		// подключать здесь то будет слишком рано
		// Используем store
		this.vue.use(store);

		// Автоматическая регистрация всех компонентов указанных в AutoRegisterPlugin
		this.vue.use(AutoRegisterPlugin);

		// Глобальные свойства
		(window as any).app = this.vue;

		//Мы здесь больше не монтируем приложение, нужно подождать .use(router)
	}

	/**
	 * Инициализация и регистрация Vue плагинов
	 */
	static #initVuePlugins(): void {
		// Регистрируем Core в Vue для доступа через DevTools
		this.#registerCoreInVue();

		// RPC Plugin
		this.vue.use(RpcService.getVuePlugin());

		// Toast уведомления
		this.vue.use(VueToast, this.config.toast);
	}

	/**
	 * Регистрирует Core в Vue для доступа через DevTools
	 * Делает все свойства и сервисы Core доступными в компонентах через this.$core
	 * и видимыми в Vue DevTools
	 */
	static #registerCoreInVue(): void {
		// Регистрируем как плагин Vue
		this.vue.use({
			install(app: VueApp) {
				// Добавляем прямую ссылку на статический класс Core
				app.config.globalProperties.$core = Core;
				// Для отображения локальных метрик компонентов в Performance DevTools
				app.config.performance = true;
			}
		});
	}

	//////////////////////////////////////

	static log(message: string, ...args: unknown[]): void {
		if (Config.debug) {
			console.log(message, ...args);
		}
		SentryService.trace(message, args.length ? args : undefined);
	}

	static warn(message: string, ...args: unknown[]): void {
		if (Config.debug) {
			console.warn(message, ...args);
		}
		SentryService.traceWarn(message, args.length ? args : undefined);
	}

	static debug(message: string, ...args: unknown[]): void {
		if (Config.debug) {
			console.debug(message, ...args);
		}
		SentryService.trace(message, args.length ? args : undefined);
	}

	/**
	 * Обработка ошибки с опциональной отправкой в Sentry
	 */
	static error(text: string, error: Error | null = null, sendToSentry: boolean = false): void {
		// Показываем тост всегда + тост еще может быть недоступен, поэтому optional-chaining
		this.showNotification?.error(text);

		if (sendToSentry && error && SentryService?.isInitialized) {
			// SentryService сам логирует в консоль
			SentryService.reportError(error,
				{ 'error.source': 'core_error' },
				{ 'core_context': { message: text } },
				'Core'
			);
		} else {
			// Логируем сами если не шлём в Sentry
			console.error(text, error);
		}
	}

	/**
	 * Стандартная обработка ApplicationError.
	 *
	 * Вызывается как из компонентов явно, так и автоматически из глобальных
	 * обработчиков SentryService при необработанных ApplicationError.
	 *
	 * - Notice  → тост warning, Sentry не трогаем
	 * - Warning → тост warning + Sentry
	 * - Error   → тост error   + Sentry
	 *
	 * Неожиданные ошибки пробрасываются дальше — их поймает глобальный обработчик.
	 *
	 * @example
	 * try {
	 *     this.localPaymentRequest.addPosition(position);
	 * } catch (e) {
	 *     Core.handleApplicationError(e);
	 * }
	 */
	static handleApplicationError(error: unknown): void {
		if (!(error instanceof ApplicationError)) {
			throw error;
		}

		if (error.severity === ErrorSeverity.Notice) {
			this.showNotification?.warning(error.message);
			return;
		}

		if (error.severity === ErrorSeverity.Warning) {
			this.showNotification?.warning(error.message);
		} else {
			this.showNotification?.error(error.message);
		}

		SentryService.reportError(
			error,
			{
				'error.source':   'client_validation',
				'error.code':     String(error.code),
				'error.severity': error.severity,
			},
			{ debugData: error.debugData },
		);
	}

	//////////////////////////////////////

	static #initTimeZone(): void {
		try {
			const stored = localStorage.getItem('user_timezone');
			if (stored) {
				this.#timeZone = TimeZone.fromLocalStorage(JSON.parse(stored));
			}
		}
		catch (e) {
			// Игнорируем ошибки парсинга
		}

		if (!this.#timeZone) {
			this.setTimeZone(TimeZone.fromBrowser());
		}
	}

	static get timeZone(): TimeZone | null {
		return this.#timeZone;
	}

	static setTimeZone(timeZone: TimeZone): void {
		this.#timeZone = timeZone;
		localStorage.setItem('user_timezone', JSON.stringify(timeZone));
	}

	//////////////////////////////////////

	static #initLiveTime(): void {
		this.#liveTime = new LiveTime();
	}

	//////////////////////////////////////

	/**
	 * Глобальный обработчик RPC ошибок
     *
     * В случае если ошибка относится к определенному типу (например нарушение уникальности)
     * или имеет низкий severity, в случае RPC ошибок, то в сентри записи не происходит.
	 *
	 * @param error - Объект ошибки от RPC
	 * @param request - Объект запроса
	 * @param silent - Подавить показ тоста (для кастомных сообщений в сервисах)
	 */
	static handleRpcError(error: any, request: any, silent: boolean = false): void {
		const errorCode = error?.code;
		let errorMessage: string | null = null;
		let sendToSentry = false;

		switch (errorCode) {
			case ErrorCodes.AUTH_REQUIRED:
				this.acl.handleInvalidSession();
				return;

			case ErrorCodes.VALIDATION_ERROR:
				errorMessage = `Ошибка валидации данных: ${error.message || 'Неизвестная ошибка'}`;
				break;

			case ErrorCodes.OBJECT_NOT_FOUND:
				errorMessage = `Объект не найден: ${error.message || 'Неизвестная ошибка'}`;
				break;

			case ErrorCodes.ACCESS_VIOLATION:
				errorMessage = `Нарушение прав доступа: ${error.message || 'Неизвестная ошибка'}`;
				break;

			case ErrorCodes.UNIQUENESS_VIOLATION:
				errorMessage = `Объект уже существует: ${error.message || 'Неизвестная ошибка'}`;
				break;

			case ErrorCodes.LINKED_ENTITY_DETECTED:
				errorMessage = `Невозможно удалить: ${error.message ?? 'Объект используется в других местах'}`;
				break;

			case ErrorCodes.OBJECT_VERSION_MISMATCH:
				errorMessage = 'Конфликт версий: Объект был изменен другим пользователем. Обновите страницу.';
				break;

			case ErrorCodes.TIMEOUT_ERROR:
				errorMessage = 'Превышено время ожидания запроса';
				sendToSentry = true;
				break;

			default:
				errorMessage = 'Ошибка сервера';
				sendToSentry = true;
		}

		// Бэк может пометить ошибку как критическую через severity
		if (error?.severity === 'error') {
			sendToSentry = true;
		}

		if (sendToSentry) {
			SentryService.reportRpcError(error, request);
		}

		if (!silent && errorMessage) {
			this.showNotification?.error(errorMessage);
		}
	}

	/**
	 * Показать уведомление пользователю, ide подскажет типы после
	 */

	static get showNotification(): ToastPluginApi {
		return this.vue?.config.globalProperties.$toast;
	}

	/**
	 * Скопировать текст в буфер обмена с уведомлением о результате
	 */
	static async copyToClipboard(text: string): Promise<void> {
		if (await Utils.copyToClipboard(text)) {
			this.showNotification.success('Скопировано в буфер обмена');
		} else {
			this.showNotification.error('Не удалось скопировать');
		}
	}

	//////////////////////////////////////

	static #loadDebugMode(): boolean {
		try {
			return localStorage.getItem('debug_mode') === 'true';
		} catch {
			return false;
		}
	}

	static toggleDebug() {
		Core.debugMode.value = !Core.debugMode.value;
		try {
			localStorage.setItem('debug_mode', String(Core.debugMode.value));
		} catch (e) {
			Core.warn('Не удалось сохранить debug_mode в localStorage', e);
		}
	}
}

export default Core;
